import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import selectors from "selectors";
import { fetchUpcomingChats } from "actions/chat";
import { Page } from "components/ui";
import UpcomingChat from "./upcoming-chat";
import UpcomingChatMobileCard from "./upcoming-chat-mobile-card";
import "./upcoming-chats.scss";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import UpcomingChatError from "./upcoming-chat-error";
import BreadCrumbs from "components/ui_new/breadcrumbs";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createWCAGTheme } from "theme/wcag-theme";

const UpcomingChats = () => {
  const translate = useTranslate();
  useDocumentTitle(translate("myAppointments"));
  const dispatch = useDispatch();
  const chats = useSelector(selectors.getUpcomingChats);
  const [isFetching, setIsFetching] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 900px)");
  const baseTheme = useTheme();
  const wcagTheme = useMemo(() => createWCAGTheme(baseTheme), [baseTheme]);

  useEffect(() => {
    setIsFetching(true);
    dispatch(fetchUpcomingChats()).finally(() => {
      setIsFetching(false);
    });
  }, [dispatch]);

  const [upcomingChatErrors, setUpcomingChatErrors] = useState(false);

  return (
    <ThemeProvider theme={wcagTheme}>
      <Page.Container>
        <Page.Header title={translate("upcomingChats")} goBack />
        <Page.Content
          isFetching={isFetching}
          isEmpty={chats.length === 0}
          emptyMessage={translate("noUpcomingChats")}
        >
        <div className="appointments-page-content" id="appointments">
          <div className="page-content-header">
            <BreadCrumbs 
              childLabel="myAppointments" 
              parentLink="/upcoming-chats" 
              parentLinkLabel="appointments" 
              title={translate("myAppointments")}
              titleClassname="appointments-title"
            />
            <h2 className="appointments-subtitle">
              {translate("youHaveUpcomingAppointments").replace(
                "{0}",
                chats.filter(chat => {
                  const meeting_status =
                    chat.chats[0]?.chat_details?.meeting_status;
                  return (
                    meeting_status &&
                    meeting_status !== "canceled" &&
                    meeting_status !== "declined" &&
                    meeting_status !== "completed"
                  );
                }).length
              )}
            </h2>
          </div>

          {!isSmallScreen ? (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="Appointments Table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">
                      <span id="date-header" className="table-header-title">
                        {translate("date")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span id="topic-header" className="table-header-title">
                        {translate("topic")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span id="with-header" className="table-header-title">
                        {translate("with")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span id="status-header" className="table-header-title">
                        {translate("status")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span id="starts-in-header" className="table-header-title">
                        {translate("startsIn")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span
                        id="description-header"
                        className="table-header-title"
                      >
                        {translate("description")}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <span id="actions-header" className="sr-only">
                        {translate("actions")}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chats
                    // .sort(
                    //   (a, b) =>
                    //     new Date(a.chats[0].chat_details.date_time).getTime() -
                    //     new Date(b.chats[0].chat_details.date_time).getTime()
                    // )
                    .map((chat, index) => {
                      if (
                        chat.slot_id != null &&
                        chat.chats[0] != null &&
                        chat.chats[0].chat_details != null &&
                        chat.chats[0].topic_details != null
                      ) {
                        return (
                          <UpcomingChat key={chat.slot_id || index} {...chat} />
                        );
                      } else if (!upcomingChatErrors) {
                        setUpcomingChatErrors(true);
                      }
                      return null;
                    })}
                  {upcomingChatErrors && <UpcomingChatError />}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="mobile-cards-container">
              {chats.map((chat, index) => {
                if (
                  chat.slot_id != null &&
                  chat.chats[0] != null &&
                  chat.chats[0].chat_details != null &&
                  chat.chats[0].topic_details != null
                ) {
                  return (
                    <UpcomingChatMobileCard
                      key={chat.slot_id || index}
                      {...chat}
                    />
                  );
                } else if (!upcomingChatErrors) {
                  setUpcomingChatErrors(true);
                }
                return null;
              })}
              {upcomingChatErrors && <UpcomingChatError />}
            </div>
          )}
        </div>
      </Page.Content>
    </Page.Container>
    </ThemeProvider>
  );
};

export default UpcomingChats;
